import type { AxiosError } from 'axios'

export const CLOVER_EXTENSION_MESSAGE_SOURCE = 'bookkeeper-clover-extension'

export type CloverExtensionErrorCode =
  | 'NOT_INSTALLED'
  | 'LOGIN_REQUIRED'
  | 'EXTRACTION_FAILED'
  | 'TIMEOUT'

export type CloverExtensionTokens = {
  sessionToken: string
  sessionContext: string
  csrfToken: string
  merchantId?: string
  giftcardsPlaySession?: string
  giftcardsAuthToken?: string
  giftcardsCookiesFound?: boolean
  giftcardsCookieNames?: string[]
  giftcardsCookieCount?: number
}

export class CloverExtensionError extends Error {
  code: CloverExtensionErrorCode

  constructor(code: CloverExtensionErrorCode, message: string) {
    super(message)
    this.name = 'CloverExtensionError'
    this.code = code
  }
}

type ExtensionMessage = {
  source?: string
  type?: string
  requestId?: string
  success?: boolean
  code?: CloverExtensionErrorCode
  error?: string
  tokens?: {
    sessionToken: string
    sessionContext: string
    csrfToken: string
    giftcardsPlaySession?: string
    giftcardsAuthToken?: string
  }
  merchantId?: string
  giftcardsCookiesFound?: boolean
  giftcardsCookieNames?: string[]
  giftcardsCookieCount?: number
  enabled?: boolean
  lastTickAt?: string | number | null
  lastLoggedInAt?: string | number | null
  loggedOutDetectedAt?: string | number | null
  noTab?: boolean
  hosts?: { www?: boolean; giftcards?: boolean }
  status?: CloverKeepaliveStatus
}

let extensionReady = false

function isExtensionMessage(data: unknown): data is ExtensionMessage {
  return (
    !!data &&
    typeof data === 'object' &&
    'source' in data &&
    (data as ExtensionMessage).source === CLOVER_EXTENSION_MESSAGE_SOURCE
  )
}

function markExtensionReady() {
  extensionReady = true
}

// Catch early READY announcements fired before React mounts.
if (typeof window !== 'undefined') {
  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
      return
    }
    if (event.data.type === 'CLOVER_EXTENSION_READY') {
      markExtensionReady()
    }
  })
}

/**
 * Ping the content script and wait for a READY response.
 */
function probeCloverExtension(timeoutMs = 1500): Promise<boolean> {
  if (extensionReady) {
    return Promise.resolve(true)
  }

  const requestId = crypto.randomUUID()

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage)
      resolve(false)
    }, timeoutMs)

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
        return
      }

      if (event.data.type !== 'CLOVER_EXTENSION_READY') {
        return
      }

      if (event.data.requestId && event.data.requestId !== requestId) {
        return
      }

      markExtensionReady()
      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      resolve(true)
    }

    window.addEventListener('message', onMessage)

    window.postMessage(
      {
        source: CLOVER_EXTENSION_MESSAGE_SOURCE,
        type: 'CLOVER_EXTENSION_PING',
        requestId,
      },
      window.location.origin
    )
  })
}

/**
 * Returns true once the Clover extension content script has announced readiness.
 */
export function isCloverExtensionAvailable(timeoutMs = 1500): Promise<boolean> {
  return probeCloverExtension(timeoutMs)
}

/**
 * Request Clover tokens from the installed Chrome extension.
 */
export async function requestCloverTokensFromExtension(
  timeoutMs = 15000
): Promise<CloverExtensionTokens> {
  const requestId = crypto.randomUUID()

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage)
      reject(
        new CloverExtensionError(
          'NOT_INSTALLED',
          `Clover Token Extractor extension did not respond. Reload the extension at chrome://extensions/, refresh this page (${window.location.origin}), and try again.`
        )
      )
    }, timeoutMs)

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
        return
      }

      if (
        event.data.type !== 'CLOVER_EXTRACT_TOKENS_RESPONSE' ||
        event.data.requestId !== requestId
      ) {
        return
      }

      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      markExtensionReady()

      if (event.data.success && event.data.tokens) {
        resolve({
          sessionToken: event.data.tokens.sessionToken,
          sessionContext: event.data.tokens.sessionContext,
          csrfToken: event.data.tokens.csrfToken,
          merchantId: event.data.merchantId,
          giftcardsPlaySession: event.data.tokens.giftcardsPlaySession,
          giftcardsAuthToken: event.data.tokens.giftcardsAuthToken,
          ...(event.data.giftcardsCookiesFound !== undefined
            ? { giftcardsCookiesFound: event.data.giftcardsCookiesFound }
            : {}),
          ...(event.data.giftcardsCookieNames
            ? { giftcardsCookieNames: event.data.giftcardsCookieNames }
            : {}),
          ...(event.data.giftcardsCookieCount !== undefined
            ? { giftcardsCookieCount: event.data.giftcardsCookieCount }
            : {}),
        })
        return
      }

      const code = event.data.code ?? 'EXTRACTION_FAILED'
      const message =
        event.data.error ??
        (code === 'LOGIN_REQUIRED'
          ? 'Please log into Clover, then try again.'
          : 'Failed to extract Clover tokens.')

      reject(new CloverExtensionError(code, message))
    }

    window.addEventListener('message', onMessage)

    window.postMessage(
      {
        source: CLOVER_EXTENSION_MESSAGE_SOURCE,
        type: 'CLOVER_EXTRACT_TOKENS_REQUEST',
        requestId,
      },
      window.location.origin
    )
  })
}

export type CloverKeepaliveStatus = {
  enabled: boolean
  lastTickAt?: string | null
  lastLoggedInAt?: string | null
  loggedOutDetectedAt?: string | null
  noTab?: boolean
  hosts: { www: boolean; giftcards: boolean }
}

function toKeepaliveTimestamp(value: unknown): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value).toISOString()
  }
  if (typeof value === 'string') return value
  return null
}

/** Accept the extension's flat STATUS payload or a nested `{status}` wrapper. */
export function parseCloverKeepaliveStatus(
  data: Record<string, unknown> | ExtensionMessage,
): CloverKeepaliveStatus | null {
  const nested =
    data.status && typeof data.status === 'object'
      ? (data.status as Partial<CloverKeepaliveStatus>)
      : null
  const enabled = nested?.enabled ?? data.enabled
  if (typeof enabled !== 'boolean') {
    return null
  }
  const rawHosts = nested?.hosts ?? data.hosts
  const hostRecord =
    rawHosts && typeof rawHosts === 'object'
      ? (rawHosts as { www?: unknown; giftcards?: unknown })
      : {}
  return {
    enabled,
    lastTickAt: toKeepaliveTimestamp(nested?.lastTickAt ?? data.lastTickAt),
    lastLoggedInAt: toKeepaliveTimestamp(nested?.lastLoggedInAt ?? data.lastLoggedInAt),
    loggedOutDetectedAt: toKeepaliveTimestamp(
      nested?.loggedOutDetectedAt ?? data.loggedOutDetectedAt,
    ),
    noTab: Boolean(nested?.noTab ?? data.noTab),
    hosts: {
      www: Boolean(hostRecord.www),
      giftcards: Boolean(hostRecord.giftcards),
    },
  }
}

/**
 * Enable or disable Clover session keepalive in the extension.
 */
export async function setCloverKeepalive(
  enabled: boolean,
  timeoutMs = 5000,
): Promise<void> {
  const requestId = crypto.randomUUID()

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage)
      reject(
        new CloverExtensionError(
          'NOT_INSTALLED',
          'Clover Token Extractor extension did not respond to keepalive request.',
        ),
      )
    }, timeoutMs)

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
        return
      }

      if (
        event.data.type !== 'CLOVER_KEEPALIVE_SET_RESPONSE' ||
        event.data.requestId !== requestId
      ) {
        return
      }

      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      markExtensionReady()

      if (event.data.success) {
        resolve()
        return
      }

      const code = event.data.code ?? 'EXTRACTION_FAILED'
      reject(
        new CloverExtensionError(
          code,
          event.data.error ?? 'Failed to update Clover keepalive setting.',
        ),
      )
    }

    window.addEventListener('message', onMessage)

    window.postMessage(
      {
        source: CLOVER_EXTENSION_MESSAGE_SOURCE,
        type: 'CLOVER_KEEPALIVE_SET',
        requestId,
        enabled,
      },
      window.location.origin,
    )
  })
}

/**
 * Read keepalive state from the extension.
 */
export async function getCloverKeepaliveStatus(
  timeoutMs = 5000,
): Promise<CloverKeepaliveStatus> {
  const requestId = crypto.randomUUID()

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage)
      reject(
        new CloverExtensionError(
          'NOT_INSTALLED',
          'Clover Token Extractor extension did not respond to keepalive status request.',
        ),
      )
    }, timeoutMs)

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
        return
      }

      if (
        event.data.type !== 'CLOVER_KEEPALIVE_STATUS_RESPONSE' ||
        event.data.requestId !== requestId
      ) {
        return
      }

      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      markExtensionReady()

      if (event.data.success) {
        const status = parseCloverKeepaliveStatus(event.data)
        if (status) {
          resolve(status)
          return
        }
      }

      const code = event.data.code ?? 'EXTRACTION_FAILED'
      reject(
        new CloverExtensionError(
          code,
          event.data.error ?? 'Failed to read Clover keepalive status.',
        ),
      )
    }

    window.addEventListener('message', onMessage)

    window.postMessage(
      {
        source: CLOVER_EXTENSION_MESSAGE_SOURCE,
        type: 'CLOVER_KEEPALIVE_STATUS',
        requestId,
      },
      window.location.origin,
    )
  })
}

/**
 * True when an API error is a Clover session auth failure (not app login).
 */
export function isCloverAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return false
  }

  const axiosError = error as AxiosError
  const header = axiosError.response?.headers?.['x-external-auth-error']
  if (typeof header === 'string') {
    return header.toLowerCase() === 'clover'
  }

  const detail = axiosError.response?.data as { detail?: unknown } | undefined
  if (typeof detail?.detail === 'string') {
    return detail.detail.toLowerCase().includes('clover authentication failed')
  }

  return false
}
